import React, { useState, useMemo, useEffect } from 'react';

import { setCookie } from 'cookies-next';
import dynamic from 'next/dynamic';

import { ApiError, LoyaltyType } from '@api/index';
import TurkTelekomModal, {
  TurkTelekomModalFormData,
} from '@components/ui/modals/PromotionModal/TurkTelekom';
import Hopi, { HopiFlowProps } from '@components/ui/modals/PromotionModal/Hopi';
import KocAilem, {
  KocAilemModalFormData,
} from '@components/ui/modals/PromotionModal/KocAilem';
import Zubizu, {
  ZubizuModalFormData,
} from '@components/ui/modals/PromotionModal/Zubizu/Zubizu';
import { useBasketId, isLoyaltyValidated } from '@hooks/cache';
import { callService } from '@api/remote-call';
import { useBasketMutation } from '@hooks/useBasketInformation';
import { useKocAliemContext } from '@contexts/loyalty/KocAilem';

const ErrorModal = dynamic(() => import('@components/ui/modals/ErrorModal'), {
  ssr: false,
});

const modals = {
  TurkTelekom: TurkTelekomModal,
  TurkTelekomPrime: TurkTelekomModal,
  TurkTelekomPrimeBusiness: TurkTelekomModal,
  KocAilem: KocAilem,
  HopiKazanim: Hopi,
  Zubizu: Zubizu,
};

export type { LoyaltyType };

interface LoyaltyVerificationProps extends HopiFlowProps {
  action?: () => void;
  loyaltyType: LoyaltyType;
  onCancel?: () => void;
  hotelListing?: boolean;
  tourCard?: boolean;
}

const getTurkTelekomService = (loyaltyType: LoyaltyType) => {
  switch (loyaltyType) {
    case LoyaltyType.TURK_TELEKOM:
      return 'validateTurkTelekom';
    case LoyaltyType.TURK_TELEKOM_PRIME:
      return 'validateTurkTelekomPrime';
    case LoyaltyType.TURK_TELEKOM_PRIME_BUSINESS:
      return 'validateTurkTelekomBusinessPrime';

    default:
      return 'validateTurkTelekom';
  }
};

const LoyaltyVerifications = ({
  action,
  loyaltyType,
  onCancel,
  hotelListing,
  tourCard,
  ...rest
}: LoyaltyVerificationProps) => {
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const [showModal, setShowModal] = useState(false);
  const basketId = useBasketId();
  const { mutate: fetchBasketDetail } = useBasketMutation();

  const [loading, setLoading] = useState(false);
  const [isValidated, setIsValidate] = useState(() =>
    isLoyaltyValidated(loyaltyType)
  );
  const { setIsValidated: setIsKocAliemValidated } = useKocAliemContext();

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //@ts-ignore
  const Modal = modals[loyaltyType];
  const bid = basketId ? { basketId: basketId } : {};

  const commonActions = async (basketId: string) => {
    localStorage.setItem('basketId', basketId);
    setCookie('basketId', basketId);
    action?.();
    fetchBasketDetail(basketId);
    setIsValidate(true);
    setLoading(false);
  };

  const toggleErrorModal = () => setError((val) => !val);

  const handleTurkTelekomValidate = async (form: TurkTelekomModalFormData) => {
    const { phoneNumber, turkTelekomCode } = form;
    if (!phoneNumber || !turkTelekomCode) return handleError();

    if (phoneNumber && turkTelekomCode) {
      try {
        const { basketId: id } = await callService(
          {
            service: 'LoyaltiesService',
            method: getTurkTelekomService(loyaltyType),
            version: 'v3',
          },
          {
            number: phoneNumber.trim(),
            code: turkTelekomCode.trim(),
            ...bid,
          }
        );

        if (id) {
          commonActions(id);
        }
      } catch (error) {
        toggleErrorModal();
      }
    }
  };
  const handleKocAilemValidate = async (form: KocAilemModalFormData) => {
    const { number, type } = form;
    if (!number) return handleError();

    try {
      setLoading(true);
      const { basketId: id } = await callService(
        {
          service: 'LoyaltiesService',
          method: 'validateKocAilem',
          version: 'v3',
        },
        {
          type: getType(Number(type?.split('#')?.pop())),
          number,
          ...bid,
        }
      );

      if (id) {
        commonActions(id);
        setIsKocAliemValidated?.(true);
      }
    } catch (error) {
      setLoading(false);
      const errorMessage = (error as ApiError)?.body?.errors[0]?.detail?.split(
        '-'
      )[1];
      setErrorMessage(
        errorMessage
          ? errorMessage
          : `Koç Ailem üyeliğiniz dogrulanamamıştır. Sorularınız ve detaylı
      bilgi icin 444 0 447 Koç Ailem Müsteri Hizmetlerini
      arayabilirsiniz.`
      );
      toggleErrorModal();
    }
  };

  const getType = (type: number) => {
    switch (type) {
      case 0:
        return 'TCKN';
      case 1:
        return 'ALTINKOD';
      case 2:
        return 'KARTNO';
      default:
        return '';
    }
  };

  const handleZubizuValidate = async (form: ZubizuModalFormData) => {
    const { zubizuCode } = form;

    if (!zubizuCode) return handleError();

    try {
      const { basketId: id } = await callService(
        {
          service: 'LoyaltiesService',
          method: 'validateZubizu',
          version: 'v3',
        },
        {
          code: zubizuCode,
          ...bid,
        }
      );
      if (id) {
        commonActions(id);
      }
    } catch (error) {
      toggleErrorModal();
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSubmit = (formData: any): Promise<void> | null => {
    switch (loyaltyType) {
      case LoyaltyType.TURK_TELEKOM:
      case LoyaltyType.TURK_TELEKOM_PRIME:
      case LoyaltyType.TURK_TELEKOM_PRIME_BUSINESS:
        return handleTurkTelekomValidate(formData);
      case LoyaltyType.KOC_AILEM:
        return handleKocAilemValidate(formData);
      case LoyaltyType.ZUBIZU:
        return handleZubizuValidate(formData);
      default:
        return null;
    }
  };

  const handleCancel = (): void => {
    setShowModal(false);
    onCancel && onCancel();
  };

  const promotionType = useMemo(() => {
    const { TURK_TELEKOM_PRIME, TURK_TELEKOM_PRIME_BUSINESS } = LoyaltyType;

    const types = {
      [TURK_TELEKOM_PRIME]: 'prime',
      [TURK_TELEKOM_PRIME_BUSINESS]: 'business',
    };

    if (
      loyaltyType === TURK_TELEKOM_PRIME ||
      loyaltyType === TURK_TELEKOM_PRIME_BUSINESS
    )
      return types[loyaltyType];
  }, [loyaltyType]);

  useEffect(() => {
    if (error) {
      setShowModal(false);
    }
  }, [error]);

  const handleError = () => {
    setShowModal(false);
    toggleErrorModal();
  };

  const handleModalErrorCancel = () => {
    setShowModal(true);
    toggleErrorModal();
  };
  useEffect(() => {
    const body = document.querySelector('body');

    if (body) {
      if (loyaltyType) {
        body.style.overflow = 'hidden';
      } else {
        body.style.overflow = 'initial';
      }
    }

    return () => {
      if (body) {
        body.style.overflow = 'initial';
      }
    };
  }, [loyaltyType]);
  if (error)
    return (
      <ErrorModal
        borderRadius={8}
        onCancel={handleModalErrorCancel}
        message={errorMessage ?? 'Girdiğiniz indirim kodu hatalıdır.'}
      />
    );

  return (
    <Modal
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onSubmit={(formData: any) => handleSubmit(formData)}
      onCancel={handleCancel}
      isVisible={showModal}
      promotion={promotionType}
      isValidated={isValidated}
      onValidate={commonActions}
      isLoading={loading}
      onValidateError={handleError}
      closeModal={handleCancel}
      hotelListing={hotelListing}
      tourCard={tourCard}
      {...rest}
    />
  );
};

export default LoyaltyVerifications;
